import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import MaterialsManager from './MaterialsManager';

const MaterialsPage = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Selected course for editing materials
  const [selectedCourse, setSelectedCourse] = useState(null);

  // Student access panel
  const [studentPanel, setStudentPanel] = useState(null); // { courseId, courseName }
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const { data } = await api.get('/courses');
        setCourses(data.courses || []);
      } catch (err) {
        setError('Failed to load offerings');
      } finally {
        setLoading(false);
      }
    };
    fetchCourses();
  }, []);

  const getMaterialCount = (course) => {
    if (!course.materials?.length) return 0;
    return course.materials.reduce((sum, w) => sum + (w.videos?.length || 0), 0);
  };

  const openStudentPanel = async (course) => {
    setStudentPanel({ courseId: course._id, courseName: course.name });
    setStudentsLoading(true);
    try {
      const { data } = await api.get(`/materials/${course._id}/students`);
      setStudents(data.students);
    } catch (err) {
      setStudents([]);
    } finally {
      setStudentsLoading(false);
    }
  };

  const togglePause = async (courseId, enrollmentId) => {
    setTogglingId(enrollmentId);
    try {
      const { data } = await api.patch(`/materials/${courseId}/pause/${enrollmentId}`);
      setStudents(prev => prev.map(s =>
        s._id === enrollmentId ? { ...s, materialsPaused: data.materialsPaused } : s
      ));
    } catch (err) {
      console.error('Failed to toggle pause:', err);
    } finally {
      setTogglingId(null);
    }
  };

  // If editing a specific course's materials
  if (selectedCourse) {
    return (
      <MaterialsManager
        courseId={selectedCourse._id}
        courseName={selectedCourse.name}
        onBack={() => setSelectedCourse(null)}
      />
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="text-gray-500">Loading...</div></div>;
  }

  if (error) {
    return <div className="bg-red-50 p-6 rounded-xl border border-red-100 text-center text-red-600">{error}</div>;
  }

  const activeCourses = courses.filter(c => c.status === 'active' || c.status === 'draft');

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Materials</h1>
      <p className="text-sm text-gray-500 mb-6">
        Manage tutorial videos and resources for your offerings. Add YouTube or Google Drive links per week.
      </p>

      {activeCourses.length === 0 ? (
        <div className="bg-white p-8 rounded-xl border border-gray-100 shadow-sm text-center text-gray-500">
          No offerings yet. Create an offering first to add materials.
        </div>
      ) : (
        <div className="space-y-3">
          {activeCourses.map(course => {
            const materialCount = getMaterialCount(course);
            const weekCount = course.materials?.length || 0;

            return (
              <div key={course._id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-4 sm:p-5">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${course.status === 'active' ? 'bg-green-400' : 'bg-gray-300'}`} />
                        <h3 className="text-base font-semibold text-gray-900">{course.name}</h3>
                        {course.status === 'draft' && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">Draft</span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 mt-1 ml-4">
                        {course.danceStyle && <span>{course.danceStyle}</span>}
                        {course.levelName && <span> &middot; {course.levelName}</span>}
                        <span className="ml-3 text-gray-400">
                          {weekCount} week{weekCount !== 1 ? 's' : ''} &middot; {materialCount} video{materialCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0 ml-4 sm:ml-0">
                      {course.enrolledCount > 0 && (
                        <button
                          onClick={() => openStudentPanel(course)}
                          className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          Student Access
                        </button>
                      )}
                      <button
                        onClick={() => setSelectedCourse(course)}
                        className="px-4 py-1.5 text-sm font-medium text-brand-600 bg-brand-50 rounded-lg hover:bg-brand-100 transition-colors"
                      >
                        {materialCount > 0 ? 'Edit Materials' : 'Add Materials'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Week summary strip */}
                {weekCount > 0 && (
                  <div className="px-5 pb-4">
                    <div className="flex gap-1.5 flex-wrap">
                      {course.materials.map((week) => (
                        <span
                          key={week.week}
                          className={`px-2.5 py-1 rounded text-xs font-medium ${
                            week.week === (course.currentWeek || 1)
                              ? 'bg-rose-800 text-white'
                              : week.week < (course.currentWeek || 1)
                                ? 'bg-green-100 text-green-700'
                                : 'bg-brand-50 text-brand-600'
                          }`}
                          title={week.title || `Week ${week.week}`}
                        >
                          W{week.week}
                          <span className={`ml-1 ${week.week === (course.currentWeek || 1) ? 'text-white/70' : week.week < (course.currentWeek || 1) ? 'text-green-500' : 'text-brand-400'}`}>({week.videos?.length || 0})</span>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Student Access Modal */}
      {studentPanel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setStudentPanel(null)}>
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6" onClick={e => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-gray-900 mb-1">Student Access</h2>
            <p className="text-sm text-gray-500 mb-4">
              Control material access for <span className="font-medium text-gray-700">{studentPanel.courseName}</span>. Paused students see all materials as locked.
            </p>

            {studentsLoading ? (
              <div className="text-gray-500 text-sm py-4 text-center">Loading students...</div>
            ) : students.length === 0 ? (
              <div className="text-gray-400 text-sm py-4 text-center">No active students enrolled.</div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {students.map(enrollment => (
                  <div key={enrollment._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {enrollment.student?.name || 'Unknown'}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {enrollment.student?.email}
                        <span className="ml-2 text-gray-400">
                          {enrollment.totalClassesAttended || 0} classes &middot; {(enrollment.unlockedMaterials || []).length} weeks unlocked
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => togglePause(studentPanel.courseId, enrollment._id)}
                      disabled={togglingId === enrollment._id}
                      className={`ml-3 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        enrollment.materialsPaused
                          ? 'bg-red-100 text-red-700 hover:bg-red-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      } disabled:opacity-50`}
                    >
                      {togglingId === enrollment._id
                        ? '...'
                        : enrollment.materialsPaused
                          ? 'Paused'
                          : 'Active'
                      }
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setStudentPanel(null)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialsPage;
